import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useIsFullscreen, useIsSlideshow, useLeftSidebarId, useRightSidebarId, } from 'js/file_viewer/api/ui';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { usePreviewControls } from 'js/file_viewer/contexts/preview_controls';
import { activeEditorIdAtom } from 'js/file_viewer/core/atoms/editing';
import { currentArchivePathAtom, rootFileInfoAtom, rootFileMetadataAtom, } from 'js/file_viewer/core/atoms/metadata';
import { activeTextHighlighterIdAtom, leftSidebarIdAtom, rightSidebarIdAtom, } from 'js/file_viewer/core/atoms/plugins';
import { docCurrentPageIndexAtom, docPageNavigationAtom, isDocSidebarOpenAtom, } from 'js/file_viewer/core/atoms/ui/doc';
import { areToolsHiddenAtom, isDeleteFileModalOpenAtom, isFullScreenAtom, isSlideshowAtom, isSlideshowPausedAtom, isTitleBarRenameActiveAtom, } from 'js/file_viewer/core/atoms/ui/general';
import { areLineNumbersHiddenAtom } from 'js/file_viewer/core/atoms/ui/text';
import { zoomPercentAtom } from 'js/file_viewer/core/atoms/ui/zoom';
import { useFileViewerAtom, useFileViewerAtomValue, useSetFileViewerAtom, useSetPreviewKeyAtom, } from 'js/file_viewer/core/atoms/use_atom';
import { getPreviewKeyFromFileInfo } from 'js/file_viewer/core/data/preview_key';
import { AI_PLUGIN_ID, TRANSCRIPT_BLADE_PLUGIN_ID, INFO_BLADE_PLUGIN_ID, COMMENTS_PLUGIN_ID, FILE_ACTIVITY_PLUGIN_ID, } from 'js/file_viewer/core/data/types';
import { UserAction } from 'js/file_viewer/core/logging/constants';
import { archiveSubpathQueryParam } from 'js/file_viewer/core/middlewares/url';
import { useCurrentPreviewMetadata } from 'js/file_viewer/core/queries/preview_metadata';
import { usePageCount } from 'js/file_viewer/core/queries/riviera_preview_types/utils/ssr_doc';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { getWillCloudDocPreviewAsSsrDoc } from 'js/file_viewer/core/utils/cloud_docs';
import { useStabilizedCallback } from 'js/file_viewer/core/utils/useStabilizedCallback';
import { useCurrentPreviewKey } from 'js/file_viewer/hooks/selectors';
import { makeFilesGetMetadataBatchRequestPackage } from 'js/react_query_helpers/queries/files_get_metadata';
import { createPreviewMetadataQueryKey } from 'js/react_query_helpers/queries/preview_metadata';
/**
 * @returns a callback that closes a sidebar and logs the action.
 * It's a no-op if the given sidebarId is not already open.
 */
export const useCloseSidebarCallback = (actionContext) => {
    const { logUserAction } = useFileViewerContext();
    const leftSidebarId = useLeftSidebarId();
    const rightSidebarId = useRightSidebarId();
    const closeLeftSidebar = useSetLeftSidebarCallback(actionContext);
    const closeRightSidebar = useSetRightSidebarCallback(actionContext);
    return useStabilizedCallback((sidebarId) => {
        const userActions = {
            [TRANSCRIPT_BLADE_PLUGIN_ID]: UserAction.TranscriptHide,
            [INFO_BLADE_PLUGIN_ID]: UserAction.InfoHide,
            [COMMENTS_PLUGIN_ID]: UserAction.HideComments,
            [FILE_ACTIVITY_PLUGIN_ID]: UserAction.FileActivityHide,
            [AI_PLUGIN_ID]: UserAction.HideDropboxAI,
        };
        if (leftSidebarId === sidebarId) {
            closeLeftSidebar(undefined);
            logUserAction(userActions[sidebarId], actionContext);
        }
        else if (rightSidebarId === sidebarId) {
            closeRightSidebar(undefined);
            logUserAction(userActions[sidebarId], actionContext);
        }
    });
};
/**
 * @returns a callback that opens a left sidebar and logs the action.
 */
export const useSetLeftSidebarCallback = (actionContext) => {
    const { logUserAction } = useFileViewerContext();
    const setLeftSidebarId = useSetFileViewerAtom(leftSidebarIdAtom);
    return useStabilizedCallback((sidebarId) => {
        const openUserActions = {
            [INFO_BLADE_PLUGIN_ID]: UserAction.Info,
            [FILE_ACTIVITY_PLUGIN_ID]: UserAction.FileActivity,
        };
        setLeftSidebarId(sidebarId, { actionContext });
        if (sidebarId !== undefined) {
            logUserAction(openUserActions[sidebarId], actionContext);
        }
    });
};
/**
 * Returns a callback that opens a right sidebar and logs the action.
 */
export const useSetRightSidebarCallback = (actionContext) => {
    const { logUserAction } = useFileViewerContext();
    const setRightSidebarId = useSetFileViewerAtom(rightSidebarIdAtom);
    return useStabilizedCallback((sidebarId) => {
        const openUserActions = {
            [TRANSCRIPT_BLADE_PLUGIN_ID]: UserAction.TranscriptShow,
            [COMMENTS_PLUGIN_ID]: UserAction.ShowComments,
            [AI_PLUGIN_ID]: UserAction.DropboxAI,
        };
        setRightSidebarId(sidebarId, { actionContext });
        if (sidebarId !== undefined) {
            logUserAction(openUserActions[sidebarId], actionContext);
        }
    });
};
/**
 * @returns a callback that opens a text highlighter.
 */
export const useSetActiveTextHighlighterCallback = (_actionContext) => {
    const setActiveTextHighlighterId = useSetFileViewerAtom(activeTextHighlighterIdAtom);
    // TODO: This doesn't log anything. We should probably add logging here.
    return useStabilizedCallback((textHighlighterId) => setActiveTextHighlighterId(textHighlighterId));
};
/**
 * @returns
 *   A callback that jumps the playhead to `time` seconds in the current video or audio preview.
 *   If the current preview is not a video or audio, the callback is a no-op.
 */
export const useSeekCallback = () => {
    const player = usePreviewControls().videoPlayer;
    return useStabilizedCallback((time) => {
        if (!player) {
            return;
        }
        player.currentTime(time / 100);
        player.pause();
    });
};
/**
 * @returns
 *   A callback that seeks `delta` seconds relative to the current playhead in the current video or audio preview.
 *   If the current preview is not a video or audio, the callback is a no-op.
 */
export const useSeekRelativeCallback = () => {
    const player = usePreviewControls().videoPlayer;
    return useStabilizedCallback((delta) => {
        var _a;
        if (!player) {
            return;
        }
        if (player.currentTime() === 0) {
            // The play and pause are necessary because currentTime() will have no effect if play hasn't begun
            (_a = player
                .play()) === null || _a === void 0 ? void 0 : _a.then(() => {
                player.pause();
            }).catch(() => { });
        }
        player.currentTime(player.currentTime() + delta);
        player.pause();
    });
};
/**
 * @returns
 *   A callback that navigates to the specified page in the current document preview.
 *   If the current preview is not a document, the callback is a no-op.
 */
export const useNavigateToPageCallback = (actionContext) => {
    const { logUserAction, featureConfig } = useFileViewerContext();
    const currentPreviewKey = useCurrentPreviewKey();
    const currentPreviewMetadata = useCurrentPreviewMetadata();
    const currentPreviewType = currentPreviewMetadata.previewType;
    const docTotalPageCount = usePageCount(currentPreviewKey, currentPreviewMetadata);
    const updateDocCurrentPageIndex = useSetPreviewKeyAtom(docCurrentPageIndexAtom, currentPreviewKey);
    const cloudDocWillPreviewAsSsrDoc = getWillCloudDocPreviewAsSsrDoc(featureConfig, currentPreviewMetadata);
    const updateDocPageNavigation = useSetPreviewKeyAtom(docPageNavigationAtom, currentPreviewKey);
    return useStabilizedCallback((pageIndex, offset) => {
        if (currentPreviewType !== PreviewType.SsrDoc && !cloudDocWillPreviewAsSsrDoc) {
            return;
        }
        if (currentPreviewKey != null) {
            logUserAction(UserAction.PageJump, actionContext);
            updateDocCurrentPageIndex(pageIndex, docTotalPageCount !== null && docTotalPageCount !== void 0 ? docTotalPageCount : 0);
            updateDocPageNavigation({
                pageIndex,
                offset,
            });
        }
    });
};
/**
 * @returns
 *   A callback that pauses the current video or audio preview.
 *   If the current preview is not a video or audio, the callback is a no-op.
 */
export const usePauseCallback = () => {
    const player = usePreviewControls().videoPlayer;
    return useStabilizedCallback(() => {
        if (!player) {
            return;
        }
        player.pause();
    });
};
/**
 * @returns a callback to set title bar rename flow by focusing the title bar file name display text box.
 */
export const useToggleRenameStateCallback = () => {
    const setActive = useSetFileViewerAtom(isTitleBarRenameActiveAtom);
    return useStabilizedCallback((isActive) => {
        setActive(isActive);
    });
};
/**
 * @returns a callback for exiting the current editor.
 */
export const useExitEditModeCallback = () => {
    const setActiveEditor = useSetFileViewerAtom(activeEditorIdAtom);
    return useStabilizedCallback((_actionContext) => {
        setActiveEditor(undefined);
    });
};
/**
 * @returns a callback to set the active editor.
 */
export const useSetActiveEditorCallback = () => {
    const setActiveEditor = useSetFileViewerAtom(activeEditorIdAtom);
    return useStabilizedCallback((editorId, _actionContext, pluginActivationParams) => {
        setActiveEditor(editorId, pluginActivationParams);
    });
};
/**
 * @returns a callback to update whether the delete file modal is open or closed.
 */
export const useSetDeleteFileModalOpenCallback = () => {
    const setDeleteFileModalOpen = useSetFileViewerAtom(isDeleteFileModalOpenAtom);
    return useStabilizedCallback((isOpen) => setDeleteFileModalOpen(isOpen));
};
/**
 * @returns a callback that reset the File Viewer's server state. Preview metadata will be refetched, which may
 *   trigger additional server requests and UI changes. Optionally takes new file info, which will be used when
 *   refetching preview metadata.
 */
export const useResetPreviewCallback = () => {
    const { apiV2ClientShim, optionalIOClients: { navigationClient }, } = useFileViewerContext();
    const queryClient = useQueryClient();
    const [rootFile, setRootFile] = useFileViewerAtom(rootFileInfoAtom);
    const filesGetMetadataBatchRequestPackage = React.useMemo(() => makeFilesGetMetadataBatchRequestPackage(apiV2ClientShim.filesGetMetadataBatch), [apiV2ClientShim.filesGetMetadataBatch]);
    const setFileMetadata = useSetFileViewerAtom(rootFileMetadataAtom);
    return useStabilizedCallback((newFileInfo) => {
        var _a;
        const file = newFileInfo || rootFile;
        if (file) {
            // We force the file viewer to reload preview metadata from the server with `queryClient.invalidateQueries`.
            // `updateFiles` requests that the file viewer's collection context do the same for all files in the
            // same context. This is so if the user backs out of the file viewer into the collection, the prefetched data there
            // matches the force-reloaded data used in the file viewer.
            (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.updateFiles) === null || _a === void 0 ? void 0 : _a.call(navigationClient);
            const newPreviewKey = getPreviewKeyFromFileInfo(file);
            setRootFile({ ...file, previewKey: newPreviewKey });
            const getMetadataBatchArgs = {
                apiArg: {
                    paths: [file.file_id],
                },
                pkgArg: null,
            };
            filesGetMetadataBatchRequestPackage
                .fetchQuery(queryClient, getMetadataBatchArgs)
                .then((result) => {
                var _a, _b, _c, _d, _e;
                if (!((_a = result.apiData) === null || _a === void 0 ? void 0 : _a.entries) || result.apiData.entries.length === 0) {
                    return;
                }
                const metadata = result.apiData.entries[0];
                if ((metadata === null || metadata === void 0 ? void 0 : metadata['.tag']) !== 'file' || !file.sj_id) {
                    return;
                }
                setFileMetadata({
                    ...metadata,
                    file_id: file.file_id,
                    file_name: metadata.name,
                    readOnly: (_e = (_c = (_b = metadata.sharing_info) === null || _b === void 0 ? void 0 : _b.read_only) !== null && _c !== void 0 ? _c : (_d = metadata.sharing_info) === null || _d === void 0 ? void 0 : _d.is_parent_shared_folder_read_only) !== null && _e !== void 0 ? _e : false,
                });
            });
            queryClient.invalidateQueries({
                queryKey: createPreviewMetadataQueryKey({ files: [file], include_dimensions: true }),
            });
        }
    });
};
/**
 * @returns a callback that closes the current File Viewer, if it can be closed.
 */
export const useCloseViewerCallback = () => {
    const { logUserAction, optionalIOClients: { navigationClient, urlClient }, } = useFileViewerContext();
    const rootFileInfo = useFileViewerAtomValue(rootFileInfoAtom);
    const [currentArchivePath, setCurrentArchivePath] = useFileViewerAtom(currentArchivePathAtom);
    return useStabilizedCallback((actionContext) => {
        var _a;
        logUserAction(UserAction.CloseViewer, actionContext);
        if (rootFileInfo && (currentArchivePath === null || currentArchivePath === void 0 ? void 0 : currentArchivePath.length)) {
            // we are inside an archive -- go up one level instead of closing the file viewer
            const newPath = currentArchivePath.slice(0, -1);
            setCurrentArchivePath(newPath, urlClient, {
                fromUrlChanged: false,
                isDir: true,
            });
            return;
        }
        // on close, clear out the query string so if we navigate to the same archive file again, we go to the root
        // instead of any particular folder/file we were looking at before
        urlClient === null || urlClient === void 0 ? void 0 : urlClient.setQueryStringParameter(archiveSubpathQueryParam, null);
        (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.closeViewer) === null || _a === void 0 ? void 0 : _a.call(navigationClient);
    });
};
/**
 * @returns Two callbacks that flip to the next or previous file, if the File Viewer is in a file flipping context.
 */
export const useFileFlippingCallbacks = () => {
    const { logUserAction, optionalIOClients: { navigationClient }, } = useFileViewerContext();
    return {
        flipToNextFile: useStabilizedCallback((actionContext) => {
            var _a;
            logUserAction(UserAction.FlipNext, actionContext);
            (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.flipToNextFile) === null || _a === void 0 ? void 0 : _a.call(navigationClient);
        }),
        flipToPreviousFile: useStabilizedCallback((actionContext) => {
            var _a;
            logUserAction(UserAction.FlipPrevious, actionContext);
            (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.flipToPreviousFile) === null || _a === void 0 ? void 0 : _a.call(navigationClient);
        }),
        flipToNextSlideshowFile: useStabilizedCallback((actionContext) => {
            var _a;
            logUserAction(UserAction.FlipNextSlideshow, actionContext);
            (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.flipToNextSlideshowFile) === null || _a === void 0 ? void 0 : _a.call(navigationClient);
        }),
        flipToPreviousSlideshowFile: useStabilizedCallback((actionContext) => {
            var _a;
            logUserAction(UserAction.FlipPreviousSlideshow, actionContext);
            (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.flipToPreviousSlideshowFile) === null || _a === void 0 ? void 0 : _a.call(navigationClient);
        }),
    };
};
/**
 * @returns a callback that enters fullscreen mode for the current preview.
 */
export const useEnterFullScreenCallback = () => {
    const { logUserAction, optionalIOClients: { fullScreenClient }, } = useFileViewerContext();
    const setIsFullscreen = useSetFileViewerAtom(isFullScreenAtom);
    const setZoom = useUpdateZoomCallback();
    const onExitFullScreenCallback = useExitFullScreenCallback();
    return useStabilizedCallback(({ actionContext, skipAction }) => {
        logUserAction(UserAction.ToggleFullscreenOn, actionContext);
        fullScreenClient === null || fullScreenClient === void 0 ? void 0 : fullScreenClient.enterFullScreen(() => onExitFullScreenCallback({ actionContext, skipAction }), !!skipAction);
        setIsFullscreen(true);
        // Reset the zoom level to fitted size for full screen
        setZoom({ zoomType: 'fitToPage' });
    });
};
/**
 * @returns a callback that exits fullscreen mode for the current preview.
 */
export const useExitFullScreenCallback = () => {
    const { logUserAction, optionalIOClients: { fullScreenClient }, } = useFileViewerContext();
    const setIsFullscreen = useSetFileViewerAtom(isFullScreenAtom);
    const setIsSlideshow = useSetFileViewerAtom(isSlideshowAtom);
    return useStabilizedCallback(({ actionContext, skipAction }) => {
        logUserAction(UserAction.ToggleFullscreenOff, actionContext);
        fullScreenClient === null || fullScreenClient === void 0 ? void 0 : fullScreenClient.exitFullScreen(!!skipAction);
        setIsSlideshow(false);
        setIsFullscreen(false);
    });
};
/**
 * @returns a callback that enters slideshow mode for the current preview.
 */
export const useEnterSlideshowCallback = () => {
    const { logUserAction, optionalIOClients: { preferencesClient }, } = useFileViewerContext();
    const enterFullScreen = useEnterFullScreenCallback();
    const setIsSlideshow = useSetFileViewerAtom(isSlideshowAtom);
    const setIsSlideshowPaused = useSetFileViewerAtom(isSlideshowPausedAtom);
    const isFullScreen = useIsFullscreen();
    return useStabilizedCallback(({ actionContext, skipAction }) => {
        var _a;
        logUserAction(UserAction.ToggleSlideshowOn, actionContext);
        (_a = preferencesClient === null || preferencesClient === void 0 ? void 0 : preferencesClient.ui) === null || _a === void 0 ? void 0 : _a.setHiddenState({ isHidden: true });
        if (!isFullScreen) {
            enterFullScreen({ actionContext, skipAction });
        }
        setIsSlideshow(true);
        setIsSlideshowPaused(false);
    });
};
/**
 * @returns a callback that exits slideshow mode for the current preview.
 */
export const useExitSlideshowCallback = () => {
    const { logUserAction } = useFileViewerContext();
    const exitFullScreen = useExitFullScreenCallback();
    return useStabilizedCallback(({ actionContext, skipAction }) => {
        logUserAction(UserAction.ToggleSlideshowOff, actionContext);
        exitFullScreen({ actionContext, skipAction });
    });
};
/**
 * @returns a callback that toggles slideshow mode for the current preview.
 */
export const useToggleSlideshowCallback = () => {
    const enterSlideshow = useEnterSlideshowCallback();
    const exitSlideshow = useExitSlideshowCallback();
    const isSlideshow = useIsSlideshow();
    return useStabilizedCallback((arg) => {
        if (isSlideshow) {
            exitSlideshow(arg);
        }
        else {
            enterSlideshow(arg);
        }
    });
};
/**
 * @returns a callback that toggles fullscreen mode for the current preview.
 */
export const useToggleFullScreenCallback = () => {
    const enterFullScreen = useEnterFullScreenCallback();
    const exitFullScreen = useExitFullScreenCallback();
    const isFullScreen = useIsFullscreen();
    return useStabilizedCallback((arg) => {
        if (isFullScreen) {
            exitFullScreen(arg);
        }
        else {
            enterFullScreen(arg);
        }
    });
};
/**
 * @returns A callback that toggles the visibility of line numbers in text previews.
 */
export const useToggleLineNumbersCallback = (actionContext) => {
    const { logUserAction } = useFileViewerContext();
    const [areLineNumbersHidden, setAreLineNumbersHidden] = useFileViewerAtom(areLineNumbersHiddenAtom);
    return useStabilizedCallback(() => {
        setAreLineNumbersHidden(!areLineNumbersHidden);
        logUserAction(areLineNumbersHidden ? UserAction.ShowLineNumbers : UserAction.HideLineNumbers, actionContext);
    });
};
export const useSetIsDocSidebarOpen = (userActionContext) => {
    const { optionalIOClients: { preferencesClient }, logUserAction, } = useFileViewerContext();
    const setIsDocSidebarOpen = useSetFileViewerAtom(isDocSidebarOpenAtom);
    return useStabilizedCallback((isDocSidebarOpen) => {
        var _a;
        setIsDocSidebarOpen(isDocSidebarOpen);
        logUserAction(isDocSidebarOpen ? UserAction.PageSelector : UserAction.HidePageSelector, userActionContext);
        // TODO: Investigate if we want to use atomWithWebStorage rather than syncing with preferencesClient.
        (_a = preferencesClient === null || preferencesClient === void 0 ? void 0 : preferencesClient.docSidebar) === null || _a === void 0 ? void 0 : _a.setCollapsedState({ isCollapsed: !isDocSidebarOpen });
    });
};
/**
 * @returns a callback that updates the zoom scale factor for the current preview.
 */
export const useUpdateZoomCallback = () => {
    return useSetFileViewerAtom(zoomPercentAtom);
};
/**
 * @returns a callback that hides the tools in the current preview.
 */
export const useHideToolsCallback = (userActionContext) => {
    const { logUserAction } = useFileViewerContext();
    const setAreToolsHidden = useSetFileViewerAtom(areToolsHiddenAtom);
    return useStabilizedCallback(() => {
        setAreToolsHidden(true);
        logUserAction(UserAction.HideTools, userActionContext);
    });
};
/**
 * @returns a callback that navigates to a new path within an archive file. No-op if the current preview is not
 * an archive.
 */
export const useUpdateArchiveFileCurrentPathCallback = () => {
    const { optionalIOClients: { urlClient }, } = useFileViewerContext();
    const setCurrentArchivePath = useSetFileViewerAtom(currentArchivePathAtom);
    return useStabilizedCallback((currentPath, isDir, fromUrlChanged = false) => {
        setCurrentArchivePath(currentPath, urlClient, {
            isDir,
            fromUrlChanged,
        });
    });
};
/**
 * @returns a callback that opens a URL in a new tab.
 */
export const useOpenUrlCallback = () => {
    const { optionalIOClients: { navigationClient }, } = useFileViewerContext();
    return useStabilizedCallback((url) => {
        var _a;
        (_a = navigationClient === null || navigationClient === void 0 ? void 0 : navigationClient.openUrl) === null || _a === void 0 ? void 0 : _a.call(navigationClient, url);
    });
};
/**
 * @returns a callback that uses the fileActionsClient to initiate the delete
 * flow for the current file.
 */
export const useDeleteFileCallback = () => {
    const { logUserAction, optionalIOClients: { fileActionsClient }, } = useFileViewerContext();
    const setDeleteFileModalOpen = useSetFileViewerAtom(isDeleteFileModalOpenAtom);
    return useStabilizedCallback((actionContext, key) => {
        var _a;
        logUserAction(UserAction.DeleteStart, actionContext, { key });
        const onDelete = () => { };
        const onDeleteSuccess = () => {
            logUserAction(UserAction.DeleteSuccess, actionContext);
        };
        const onDeleteFailed = () => {
            logUserAction(UserAction.DeleteFailed, actionContext);
        };
        const onDeleteFailedNoPermission = () => {
            logUserAction(UserAction.DeleteFailedNoPermission, actionContext);
        };
        const onDeleteCancel = () => {
            logUserAction(UserAction.DeleteCancel, actionContext);
        };
        const isDeleteFileModalOpen = (isOpen) => {
            setDeleteFileModalOpen(isOpen);
        };
        (_a = fileActionsClient === null || fileActionsClient === void 0 ? void 0 : fileActionsClient.delete) === null || _a === void 0 ? void 0 : _a.call(fileActionsClient, {
            onDelete,
            onDeleteSuccess,
            onDeleteFailed,
            onDeleteFailedNoPermission,
            onDeleteCancel,
            setDeleteFileModalOpen: isDeleteFileModalOpen,
        });
    });
};
//# sourceMappingURL=navigation.preenvesnextjs.map
